import { useMemo, useState } from 'react';
import { InlineStack, Select, Text } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useDeliveryFiltersOptional } from '@/services/db/delivery/queries';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import type { ReactNode } from 'react';
import { segmentablePickerData } from './message-picker-data';
import { NewsletterSelect } from './newsletter-select';
import { NewsletterVariationSelect } from './newsletter-variation-select';
import { ALL_VARIATIONS_VALUE, newsletterVariationOptions, toWireId } from './newsletter-recipients-options';

/** The editing state a newsletter-recipients leaf holds. */
export type NewsletterRecipientsRowState = {
  newsletterId: number | string | null;
  templateId: number | string | null;
  negate: boolean;
};

type NewsletterRecipientsRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's newsletter leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
};

const NEGATE_OPTIONS = [
  { value: 'false', label: 'is' },
  { value: 'true', label: 'is not' },
];

/**
 * The newsletter-recipients condition row: [type] [is / is not] a recipient
 * of [newsletter] [variation?]. The newsletter picker is server-paged and
 * server-searched; the variation select renders only when the chosen
 * newsletter has segmentable variations (from the deliveries/filters
 * catalog, like the legacy editor's filters service).
 */
export function NewsletterRecipientsRow({ id, conditionType, typeSelect }: NewsletterRecipientsRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<NewsletterRecipientsRowState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);

  // "Select a newsletter" renders only once the picker has been visited
  // (closing its dropdown) — or from the start when the row mounted with a
  // newsletter already chosen — so a freshly added row doesn't open covered
  // in red.
  const [newsletterTouched, setNewsletterTouched] = useState(() => state.newsletterId != null);
  const newsletterErrorMessage = newsletterTouched ? fieldError(errors, 'newsletterId') : undefined;

  const filters = useDeliveryFiltersOptional();
  const variations = useMemo(() => {
    if (filters.data == null) {
      return [];
    }
    return newsletterVariationOptions(segmentablePickerData(filters.data).segmentable, state.newsletterId);
  }, [filters.data, state.newsletterId]);

  const handleNegateChange = (value: string | null) => {
    if (value != null && node != null) {
      updateState(node, { negate: value === 'true' });
    }
  };

  const handleNewsletterChange = (value: string) => {
    if (node != null) {
      updateState(node, { newsletterId: toWireId(value), templateId: null });
    }
  };

  const handleVariationChange = (value: string) => {
    if (node != null) {
      updateState(node, { templateId: value === ALL_VARIATIONS_VALUE ? null : toWireId(value) });
    }
  };

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-negate-select"
        ariaLabel="Negate"
        size="sm"
        options={NEGATE_OPTIONS}
        value={state.negate ? 'true' : 'false'}
        onChange={handleNegateChange}
        isDisabled={meta.isDisabled}
      />
      <Text as="span">a recipient of</Text>
      <NewsletterSelect
        value={state.newsletterId}
        error={newsletterErrorMessage}
        onChange={handleNewsletterChange}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setNewsletterTouched(true);
          }
        }}
        disabled={meta.isDisabled}
      />
      {variations.length > 0 ? (
        <NewsletterVariationSelect
          variations={variations}
          value={state.templateId}
          onChange={handleVariationChange}
          disabled={meta.isDisabled}
        />
      ) : null}
    </InlineStack>
  );
}
